<script>
/**
 * Overview
 * @version 1.0.0
 * @since 2.3.0
 */
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';

import { wizardListenerMixin } from '@/mixins/wizard';
import { LOADING_KEY, recipeTypes } from '@/util/constants';
import { getCurrency } from '@/util/utils';

import EzEntityInfo from '@/components/ui/EntityInfo';
import EzButton from '@/components/ui/Button';
import VPrice from '@/components/v3/elements/VPrice';
import Paper from '@/components/layout/WizardOverviewPaper.vue';
import EzLoader from '@/components/ui/Loader/EzLoader';

import flash from '@/components/ui/FlashMessage';
import { RECIPE_STEP_1, RECIPE_STEP_2 } from './steps';

export default {
  mixins: [wizardListenerMixin],
  components: {
    EzEntityInfo,
    EzButton,
    EzLoader,
    VPrice,
    Paper,
  },
  data() {
    return {
      itemsListExpanded: false,
      recipeTypes,
    };
  },
  computed: {
    ...mapState('recipes', ['draft']),
    ...mapGetters('loading', ['isSomeLoading']),
    ...mapGetters('defaultImages', ['getDefaultImage']),
    defaultImage() {
      return this.getDefaultImage('category');
    },
    isLoading() {
      return this.isSomeLoading([LOADING_KEY.VENUE_CREATE_RECIPE]);
    },
    products() {
      return this.draft.items;
    },
    listItems() {
      if (!this.itemsListExpanded) return this.products.slice(0, 8);
      return this.products;
    },
    expandButtonCopy() {
      return this.itemsListExpanded ? 'Collapse List' : 'Expand List';
    },
    expandButtonIcon() {
      return this.itemsListExpanded ? 'angle-up' : 'angle-down';
    },
    currency() {
      return getCurrency() || {};
    },
    isIngredient() {
      return this.draft.type === 'ingredient';
    },
  },
  methods: {
    ...mapActions('recipes', ['venueCreateRecipe', 'venueUpdateRecipe']),
    ...mapMutations('recipes', ['CLEAR_DRAFT']),
    onPreviousStep() {
      this.$emit('stepBack');
    },
    async onNextStep() {
      const body = {
        ...this.draft,
        ...(this.draft.quantity ? { quantity: this.draft.quantity } : { quantity: '' }),
        ...(this.draft.salesPrice ? { salesPrice: this.draft.salesPrice } : { salesPrice: 0 }),
        recipeItems: this.draft.items.map(item => {
          delete item.id;
          return {
            ...item,
          };
        }),
      };
      const formData = new FormData();
      Object.keys(body).forEach(key => {
        if (body[key] !== undefined && key !== 'recipeItems' && key !== 'image') {
          formData.append(key, body[key]);
        }
      });
      if (this.draft.image) {
        formData.append(
          'image',
          new File([this.draft.image], 'recipe-image.png', {
            type: 'image/png',
          }),
        );
      }
      body.recipeItems.forEach((item, index) => {
        Object.keys(item).forEach(itemKey => {
          if (item[itemKey] !== undefined) {
            formData.append(`recipeItems[${index}][${itemKey}]`, item[itemKey]);
          }
        });
      });
      try {
        if (this.draft.id) {
          await this.venueUpdateRecipe({
            body: formData,
            id: this.draft.id,
          });
          flash.success({ title: 'Recipe successfully updated!' });
        } else {
          await this.venueCreateRecipe({
            body: formData,
          });
          flash.success({ title: 'Recipe successfully created!' });
        }
        this.CLEAR_DRAFT();
        this.$router.push({ name: 'venue-recipes' });
      } catch (e) {
        flash.error({
          title: 'Something went wrong',
          message: e.response?.data?.error?.message || '',
        });
      }
    },
    editInfo() {
      this.$emit('stepBack', RECIPE_STEP_1);
    },
    editProducts() {
      this.$emit('stepBack', RECIPE_STEP_2);
    },
    toggleListExpanded() {
      this.itemsListExpanded = !this.itemsListExpanded;
    },
  },
  async created() {
    this.enableNextStep();
  },
};
</script>

<template>
  <div class="create-order">
    <div class="create-order__main-content">
      <paper class="venue-section">
        <template #actions>
          <ez-button type="link" formType="button" @click="editInfo">
            <font-awesome-icon icon="pen" />
            <span>Edit</span>
          </ez-button>
        </template>
        <ez-entity-info
          :imgUrl="draft.image || defaultImage"
          imgHeight="64px"
          imgWidth="64px"
          imageBorderRadius="50%"
          imageHasBorder
        >
          <template>
            <span>
              {{ draft?.name }}
            </span>
          </template>
        </ez-entity-info>
        <hr />
        <div class="info info--vertical">
          <small>Type</small>
          <span>{{ recipeTypes.filter(item => item.id === draft.type)[0]?.name }}</span>
        </div>
        <div class="info info--vertical">
          <small>Unit</small>
          <span>{{ draft.unit?.name }}</span>
        </div>
        <div v-if="draft.salesPrice !== null" class="info info--vertical">
          <small>Target Menu Item Sales Price</small>
          <span>{{ draft.salesPrice }}</span>
        </div>
      </paper>

      <paper>
        <template #title>Products ({{ products.length }})</template>
        <template #actions>
          <ez-button type="link" formType="button" @click="editProducts">
            <font-awesome-icon icon="pen" />
            <span>Edit</span>
          </ez-button>
        </template>
        <div class="products-table--mobile">
          <div
            v-for="item in listItems"
            :key="item.id"
            class="product-item"
            :id="`product-item-${item.id}`"
          >
            <div class="first-row">
              <ez-entity-info imgWidth="2.5rem" imgHeight="2.5rem" :imgUrl="item.image">
                <div class="product-info mr-8" :title="item?.name">
                  <span>
                    {{ item?.name }}
                  </span>
                  <span class="product-info-secondary">
                    <span class="product-info-secondary--category category-badge">
                      {{ item.unit.label }}
                    </span>
                  </span>
                </div>
              </ez-entity-info>
            </div>
            <hr />
            <div class="second-row">
              <div class="quantity-input">
                <span>{{ item.quantity }}</span>
                <span class="unit-label" v-if="!isIngredient">{{ item.unit?.label }}</span>
                <span class="unit-label" v-else>{{ draft.unit?.label }}</span>
              </div>
              <v-price
                v-if="!isIngredient"
                :price="item.price * item.quantity || 0"
                :unit="(draft.unit || {})?.label"
                :currency="item.currency || currency || {}"
                flexRow
              />
              <v-price
                v-else
                :price="item.price / item.quantity || 0"
                :unit="(draft.unit || {})?.label"
                :currency="item.currency || currency || {}"
                flexRow
              />
            </div>
          </div>
        </div>
        <template #footer>
          <div v-if="draft.items.length > 8" class="u-flex-h-center mt-16">
            <ez-button @click="toggleListExpanded" type="link" formType="button">
              <span>{{ expandButtonCopy }}</span>
              <font-awesome-icon class="ml-8" :icon="expandButtonIcon" />
            </ez-button>
          </div>
        </template>
      </paper>
      <ez-loader :show="isLoading">Loading...</ez-loader>
    </div>
  </div>
</template>

<style scoped lang="scss">
.create-order {
  width: 100%;
  display: flex;
  @media (max-width: 768px) {
    flex-direction: column;
  }

  &__main-content {
    width: 100%;
  }
}

.venue-section :deep() {
  .overview-paper__content {
    padding-bottom: 12px;
  }

  .entity-info {
    flex-direction: column;
    align-items: center;
    margin-right: 24px;

    &__text {
      justify-content: center;
      margin-left: 0;
      margin-top: 12px;
      @include font-size(16px, 22px);
      font-weight: 600;
      color: $color-gray-25;
    }
  }

  .ref-number {
    &__label {
      @include font-size(12px, 18px);
      color: $color-gray-6C;
    }

    &__value {
      @include font-size(14px, 20px);
      color: $color-gray-25;
    }
  }
}

.products-table {
  .quantity-cell {
    width: 64px + $spacing-32;

    .quantity-input {
      :deep() .input-group {
        input {
          color: $color-primary-blue;
          font-weight: bold;
        }

        button {
          display: none;
        }
      }
    }
  }

  .pricePerUnit-cell {
    width: 90px + $spacing-32;

    :deep() .v-price__price {
      font-weight: bold;

      &:not(.v-price__market-price--row, .v-price__unit--row) {
        color: $color-gray-25;
      }
    }
  }

  .discount-cell {
    width: 95px + $spacing-32;
    overflow: visible;

    :deep() {
      .mask-input {
        &__input {
          @include font-size(16px, 20px);
        }

        &__prefix {
          color: $color-gray-6C;
          font-weight: bold;
        }
      }

      .mark-input__error {
        display: none;
      }
    }
  }

  .total-cell {
    width: 125px;
    overflow: visible;

    :deep() span {
      font-weight: bold;
      color: $color-gray-25;
    }
  }

  .product-cell {
    .product-info {
      display: flex;
      flex-direction: column;

      span {
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
      }

      &__unit {
        color: $color-gray-25;
      }
    }
  }
  tbody tr.item-bonus {
    background-color: $color-pastel-blue;
    td:first-child {
      padding-left: 8px;
    }
    td:last-child {
      padding-right: 8px;
    }
    :deep() .has-border {
      border: 1px solid $color-primary-blue;
    }
    .additional-info--bonus {
      color: $color-gray-6C;
      font-size: 12px;
      font-weight: 500;
      line-height: 14px;
      border-bottom: 2px dotted #a3bbff;
      color: $color-primary-blue;
    }
  }
}

.note-options {
  .note-option {
    &__label {
      @include font-size(12px, 22px);
      font-weight: 600;
      color: $color-gray-25;
    }
  }
}

.info-icon {
  width: $spacing-16;
  height: $spacing-16;
}

:deep() .loader {
  @extend %whole-screen;
}

:deep() .mask-input-wrapper {
  .mask-input__input {
    text-align: center;
  }
}

.ez-info-panel {
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: -28px;
  align-items: center;
  &__title {
    @extend %flex-center;
    margin-bottom: 1.25rem;
    h3 {
      @include font-size(20px);
      font-weight: 500;
      margin: 0;
    }
  }
  &__item {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    small {
      @extend %gray-label;
      font-weight: 500;
      margin-bottom: 0.5rem;
    }
    span {
      @include font-size(14px);
      white-space: pre-line;
    }
    a {
      color: #4d7cfe;
    }
  }
  &__contact {
    display: flex;
    margin-top: 1rem;

    &:not(:last-child) {
      padding-bottom: 0.5rem;
    }
  }
  &__close-button {
    margin-left: auto;
    cursor: pointer;
    svg {
      fill: $color-gray-6C;
    }
  }
  .venue-info {
    margin-top: -16px;
  }
}

.products-table--mobile {
  padding-bottom: 32px;
  width: 100%;

  .product-item {
    padding: 12px 0;
    border-bottom: 1px solid #e1e5ed;
    hr {
      margin: 12px 0;
    }
    &:hover {
      cursor: pointer;
    }
  }

  .first-row {
    display: flex;
    align-items: center;
    line-height: 1.5;
    justify-content: space-between;

    .entity-info {
      align-items: center;
      min-width: 0;
      flex-shrink: 1;
    }

    .product-quantity {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      color: $color-gray-6C;
      font-size: 14px;
      flex-shrink: 0;

      .product-info-secondary--category {
        font-size: 12px;
      }
    }
  }

  .second-row {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .quantity__icon {
      padding-left: 4px;
    }

    .quantity-input {
      display: flex;
      align-items: center;

      :deep() .mask-input-wrapper {
        max-width: 100px;
      }

      :deep() .mask-input-wrapper .mask-input .mask-input__input {
        border-radius: 0;
        font-size: 16px;
      }
    }
    .quantity .product-card-cart {
      display: flex;
      align-items: center;
      width: 120px;
      margin-right: 12px;

      :deep() .mask-input-wrapper {
        margin: 0 8px;
      }

      :deep() .mask-input-wrapper .mask-input .mask-input__input {
        border-radius: 0;
        font-size: 16px;
      }
    }
  }

  .product-info {
    display: flex;
    flex-direction: column;
    line-height: 1.5;
    font-size: 14px;
    font-weight: 500;

    .product-info-secondary {
      display: block;
      font-size: 12px;
    }

    span {
      text-overflow: ellipsis;
      overflow: hidden;
    }
  }
}

@media (max-width: 768px) {
  .products-table--mobile {
    display: block;
  }
  :deep() .products-table {
    display: none;
  }
}
</style>
